/**
 * Number formatting helpers — Feb-2026.
 *
 * The platform standardises on **3 decimal places** (ROUND_HALF_UP) for all
 * quantity-like numbers (kg, mtr, ltr, pieces, etc.). Counting UOMs
 * (NOS/PCS/BOX/PAIR/SET/DOZ) still reject decimals at validation time, but
 * on the DISPLAY side everything that flows through `fmtQty` is rounded
 * uniformly so the UI never shows ugly floating-point trailers like
 * `1.0000000003`.
 *
 * Live-Prod-Fix (Feb-2026): use ROUND_HALF_UP (0.1235 → 0.124) to match the
 * backend `round3()` helper. `toFixed()` uses banker's rounding on some JS
 * engines which produced visible mismatches with the server.
 */

function _roundHalfUp(n, places) {
  const factor = 10 ** places;
  // The +Number.EPSILON * sign nudge handles IEEE-754 artefacts like
  // 1.005 * 100 === 100.49999999999999. Multiply by sign-aware epsilon so
  // negative numbers also round half-up away from zero on the magnitude.
  const sign = n < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(n) * factor + Number.EPSILON) / factor;
}

/**
 * Round a number to N decimal places (ROUND_HALF_UP) and trim trailing
 * zeros + a trailing decimal point. Returns a string for direct JSX use.
 * fmtQty(1.000)     -> "1"
 * fmtQty(1.250)     -> "1.25"
 * fmtQty(1.234567)  -> "1.235"
 * fmtQty(1.2345)    -> "1.235"   (half-up)
 * fmtQty("")        -> ""
 * fmtQty(null)      -> "—"
 */
export function fmtQty(v, places = 3) {
  if (v === null || v === undefined || v === "") return v === "" ? "" : "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const rounded = _roundHalfUp(n, places);
  const s = rounded.toFixed(places);
  return s.replace(/\.?0+$/, "");
}

/** Same as fmtQty but always keeps `places` zeros (e.g. for rates). */
export function fmtFixed(v, places = 3) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return _roundHalfUp(n, places).toFixed(places);
}

/**
 * Explicit 3-decimal helper (ROUND_HALF_UP). Alias for fmtQty with
 * places=3. Introduced in Feb-2026 Live-Prod-Fix #1 so callers can
 * self-document the intent in table cells etc.
 */
export const fmt3 = (v) => fmtQty(v, 3);
